import { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Session timeout configuration (in milliseconds)
const SESSION_TIMEOUT = 15 * 60 * 1000; // 15 minutes of inactivity
const WARNING_TIME = 2 * 60 * 1000; // Show warning 2 minutes before logout
const ACTIVITY_EVENTS = ['mousedown', 'keydown', 'scroll', 'touchstart', 'click'];

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);
  const [showTimeoutWarning, setShowTimeoutWarning] = useState(false);
  const [timeoutCountdown, setTimeoutCountdown] = useState(0);
  
  const timeoutRef = useRef(null);
  const warningRef = useRef(null);
  const countdownRef = useRef(null);
  const lastActivityRef = useRef(Date.now());

  // Reset the inactivity timer
  const resetTimer = useCallback(() => {
    if (!user) return;
    
    lastActivityRef.current = Date.now();
    setShowTimeoutWarning(false);
    
    // Clear existing timers
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    if (warningRef.current) clearTimeout(warningRef.current);
    if (countdownRef.current) clearInterval(countdownRef.current);
    
    // Set warning timer (fires 2 minutes before logout)
    warningRef.current = setTimeout(() => {
      setShowTimeoutWarning(true);
      setTimeoutCountdown(WARNING_TIME / 1000);
      
      // Start countdown
      countdownRef.current = setInterval(() => {
        setTimeoutCountdown(prev => {
          if (prev <= 1) {
            clearInterval(countdownRef.current);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }, SESSION_TIMEOUT - WARNING_TIME);
    
    // Set logout timer
    timeoutRef.current = setTimeout(() => {
      console.log('Session timeout - logging out due to inactivity');
      logout(true);
    }, SESSION_TIMEOUT);
  }, [user]);

  // Handle user activity
  const handleActivity = useCallback(() => {
    if (user && !showTimeoutWarning) {
      resetTimer();
    }
  }, [user, showTimeoutWarning, resetTimer]);

  // Extend session (when user clicks "Stay Logged In")
  const extendSession = useCallback(() => {
    setShowTimeoutWarning(false);
    if (countdownRef.current) clearInterval(countdownRef.current);
    resetTimer();
  }, [resetTimer]);

  // Set up activity listeners
  useEffect(() => {
    if (user) {
      ACTIVITY_EVENTS.forEach(event => {
        window.addEventListener(event, handleActivity, { passive: true });
      });
      
      // Initialize timer
      resetTimer();
      
      return () => {
        ACTIVITY_EVENTS.forEach(event => {
          window.removeEventListener(event, handleActivity);
        });
        if (timeoutRef.current) clearTimeout(timeoutRef.current);
        if (warningRef.current) clearTimeout(warningRef.current);
        if (countdownRef.current) clearInterval(countdownRef.current);
      };
    }
  }, [user, handleActivity, resetTimer]);

  useEffect(() => {
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      fetchUser();
    } else {
      setLoading(false);
    }
  }, [token]);

  const fetchUser = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`);
      setUser(response.data);
      localStorage.setItem('lastActivity', Date.now().toString());
    } catch (error) {
      console.error('Failed to fetch user:', error);
      // Check if token expired
      if (error.response?.status === 401) {
        logout(true);
      } else {
        logout();
      }
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    try {
      const response = await axios.post(`${API}/auth/login`, { email, password });
      const { access_token, user: userData } = response.data;
      
      localStorage.setItem('token', access_token);
      localStorage.setItem('lastActivity', Date.now().toString());
      axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
      setToken(access_token);
      setUser(userData);
      
      return { success: true };
    } catch (error) {
      return { 
        success: false, 
        message: error.response?.data?.detail || 'Login failed' 
      };
    }
  };

  const logout = (sessionExpired = false) => {
    // Clear all timers
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    if (warningRef.current) clearTimeout(warningRef.current);
    if (countdownRef.current) clearInterval(countdownRef.current);
    
    localStorage.removeItem('token');
    localStorage.removeItem('lastActivity');
    delete axios.defaults.headers.common['Authorization'];
    setToken(null);
    setUser(null);
    setShowTimeoutWarning(false);
    
    // Store session expired flag for login page message
    if (sessionExpired) {
      sessionStorage.setItem('sessionExpired', 'true');
    }
  };

  const value = {
    user,
    token,
    loading,
    login,
    logout,
    isAuthenticated: !!user,
    showTimeoutWarning,
    timeoutCountdown,
    extendSession
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
